import type { Card, Rank, Suit } from '../trick/cards.js';
import type { TrickPlay } from '../trick/rules.js';
import { BRIDGE_SEATS, type BridgeControllers } from '../bridge/seats.js';
import { type PirateCompletedTrick, type PirateRound, type PirateRoundPhase, type PirateSeat as Seat, pendingGuessers, pirateLegalPlays } from './round.js';

/**
 * Everything one player may see: their own hand, how many cards the others hold, the shown trump card,
 * the tricks played, and the guesses. While guessing, only the viewer's own guess is included; the
 * others appear when the fourth guess arrives. No other hand is ever included.
 */
export interface PirateView {
  readonly viewer: Seat;
  readonly phase: PirateRoundPhase;
  readonly dealer: Seat;
  readonly controllers: BridgeControllers;
  readonly cardsPerHand: number;
  readonly minRank: Rank;
  /** Cap'n's Hat round: `hand` is empty (you cannot see your own card) and `hatCards` shows the others'. */
  readonly hat: boolean;
  /** In a hat round, every OTHER seat's card still in hand; null otherwise. Never includes the viewer. */
  readonly hatCards: Readonly<Partial<Record<Seat, Card>>> | null;
  /** True when the viewer must play their hidden card now (call `playHiddenCard`; there is no choice). */
  readonly mustPlayHidden: boolean;
  readonly hand: readonly Card[];
  readonly handSizes: Readonly<Record<Seat, number>>;
  readonly trump: Suit;
  readonly trumpCard: Card;
  /** The viewer's own guess, if made. */
  readonly ownGuess: number | null;
  /** Everyone's guesses, once all four are in (null while guessing). */
  readonly guesses: Readonly<Record<Seat, number>> | null;
  /** Which seats have guessed (public: matches are on the table, just not which number). */
  readonly hasGuessed: Readonly<Record<Seat, boolean>>;
  readonly trick: readonly TrickPlay<Seat>[];
  readonly tricks: readonly PirateCompletedTrick[];
  readonly tricksWon: Readonly<Record<Seat, number>>;
  readonly turn: Seat;
  /** True when the viewer must guess now, or play a card now. */
  readonly canAct: boolean;
  readonly legalPlays: readonly Card[];
}

export function pirateViewFor(round: PirateRound, viewer: Seat): PirateView {
  const revealed = round.phase !== 'guess';
  const mustGuess = round.phase === 'guess' && pendingGuessers(round).includes(viewer);
  const mustPlay = round.phase === 'play' && round.turn === viewer;
  return {
    viewer,
    phase: round.phase,
    dealer: round.dealer,
    controllers: round.controllers,
    cardsPerHand: round.cardsPerHand,
    minRank: round.minRank,
    hat: round.hat,
    hatCards: round.hat
      ? (Object.fromEntries(BRIDGE_SEATS.filter((s) => s !== viewer && round.hands[s][0]).map((s) => [s, round.hands[s][0]])) as Partial<Record<Seat, Card>>)
      : null,
    mustPlayHidden: round.hat && mustPlay,
    hand: round.hat ? [] : round.hands[viewer],
    handSizes: Object.fromEntries(BRIDGE_SEATS.map((s) => [s, round.hands[s].length])) as Record<Seat, number>,
    trump: round.trump,
    trumpCard: round.trumpCard,
    ownGuess: round.guesses[viewer] ?? null,
    guesses: revealed ? (Object.fromEntries(BRIDGE_SEATS.map((s) => [s, round.guesses[s] as number])) as Record<Seat, number>) : null,
    hasGuessed: Object.fromEntries(BRIDGE_SEATS.map((s) => [s, round.guesses[s] !== undefined])) as Record<Seat, boolean>,
    trick: round.trick,
    tricks: round.tricks,
    tricksWon: round.tricksWon,
    turn: round.turn,
    canAct: mustGuess || mustPlay,
    // A hat round has one card and no choice; listing it would show the holder their own card.
    legalPlays: mustPlay && !round.hat ? pirateLegalPlays(round) : [],
  };
}
